import React from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Pack, REGISTRATION_STATUS } from '@root/src/types';
import { mediaBreakpoint } from '@root/src/services/responsiveProvider';
import { textH3, textP } from '@features/ui/styles/textStyles';
import {
  selectIsAuthenticated,
} from '@features/users/slices/userSlice';
import {
  selectSubscriptionPack,
  setSubscriptionPack,
} from '@features/subscriptions/slices/formSubscriptionSlice';
import { selectSerieEvents, selectSeriePacks } from '../slices/serieDetailServerSlice';
import SerieSubscriptionCartButton from './SerieSubscriptionCartButton';

const SerieSubscriptionPackSelect = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const events = useSelector(selectSerieEvents);
  const logged = useSelector(selectIsAuthenticated);
  const packs = useSelector(selectSeriePacks);
  const selectedPack = useSelector(selectSubscriptionPack);

  const eventsWithOpenRegistrationsCount = events
    .filter(({ status }) => status?.registration === REGISTRATION_STATUS.OPEN).length;
  const packsAllowed = packs.filter(({ maxChoice }) => maxChoice <= eventsWithOpenRegistrationsCount);

  const PackTag = ({ pack }: { pack: Pack }) => {
    const handlePackClick = (pack) => {
      const packToSelect = selectedPack?._idMso !== pack._idMso
        ? pack
        : null;

      dispatch(setSubscriptionPack({ pack: packToSelect }));
    };

    return (
      <StyledTag
        onClick={() => handlePackClick(pack)}
        isSelected={selectedPack?._idMso === pack._idMso}
      >
        {pack.description}
        {pack.reduction > 0 && ` (-${pack.reduction}%)`}
      </StyledTag>
    );
  };

  return logged ? (
    <>
      <Label>
        {t('serieSubscription_serieSelectPackIntro.message')}
      </Label>

      <Container>
        <PacksContainer>
          {packsAllowed.map(p => (
            <PackTag
              pack={p}
            />
          ))}
        </PacksContainer>

        <ButtonContainer>
          <SerieSubscriptionCartButton />
        </ButtonContainer>
      </Container>
    </>
  ) : <></>;
};

export default SerieSubscriptionPackSelect;

const Container = styled.div`
  display: flex;

  padding: 12px 0;

  ${mediaBreakpoint.mobile} {
    flex-direction: column;
  }
`;

const Label = styled.h3`
  ${textH3};
  color: var(--color-dark);
`;

const PacksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  margin-right: auto;

  ${mediaBreakpoint.mobile} {
    flex-direction: column;
    align-items: center;

    margin-bottom: 24px;
    margin-right: 0;
  }
`;

const StyledTag = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-self: flex-start;

  padding: 0 12px;
  min-height: 48px;

  border: 1px solid ${({ isSelected }) => (isSelected ? 'var(--color-blue)' : 'var(--color-grey2)')};
  background-color: ${({ isSelected }) => (isSelected ? 'var(--color-blue)' : 'var(--color-white)')};
  color: ${({ isSelected }) => (isSelected ? 'var(--color-white)' : 'var(--color-grey4)')};
  ${textP};
  align-items: center;
  border-radius: 200px;

  cursor: pointer;
  user-select: none;

  ${mediaBreakpoint.mobile} {
    align-self: center;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: min-content;
  padding-left: 12px;

  ${mediaBreakpoint.mobile} {
    align-self: center;

    padding-left: 0;
  }
`;
